declare const __brand: unique symbol
type Brand<B> = {[__brand]: B}

declare const __base: unique symbol

/**
 * This type is meant to add more context and type safety to a value. See the below example 
 *
 * @example
 ```ts
 type UserID = Brand<string, "UserId">
 type PostID = Brand<string, "PostId">
 type CommentID = Brand<string, "CommentId">
 type User = {
   id: UserID;
   name: string
 }
 type Post = {
   id: PostID;
   ownerId: string;
   comments: Comments[]
 }
 type Comments = {
   id: CommentID
   timestamp: string
   body: string
   authorId: UserID
 }
 async function getCommentsForPost(postId: PostID, authorId: UserID) {
   const response = await api.get(`/author/${authorId}/posts/${postId}/comments`)
   return response.data
 }
 const comments = await getCommentsForPost(user.id,post.id) // ❌ This fails since `user.id` is of type UserID and no PostID as expected
 // ^Argument of type 'UserID' is not assignable to parameter of type 'PostID'.
 //  Type 'UserID' is not assignable to type '{ __brand: "PostId"; }'.
 //    Types of property '__brand' are incompatible.
 //      Type '"UserId"' is not assignable to type '"PostId"'.
 ```
*/
//
export type Branded<T, B> = T & Brand<B> & {[__base]: T}
type UnknownBrand = Branded<unknown, unknown>
export type Unbranded<T extends UnknownBrand> = T[typeof __base]

/**
 * This higher order function returns makers of branded types.
 * @type B the brand we would like to create
 *
 * @returns A function that takes in the raw type for a brand and returns the branded type
 *
 * @example
 * ```ts
 *type UserID = Branded<string, 'UserID'>
 *const makeUserId = make<UserID>()
 *const userId: UserID = makeUserId('123-abc-456')
 *```
 */
export function make<B extends UnknownBrand>() {
  return (raw: Unbranded<B>): B => raw as B
}
